import { ClockIcon, CheckCircle2Icon, MailIcon } from "lucide-react";
import type { Affiliate } from "../types";

type AffiliatePendingStatusProps = {
  affiliate: Affiliate;
};

export const AffiliatePendingStatus = ({ affiliate }: AffiliatePendingStatusProps) => {
  return (
    <main className="flex-1 p-4 md:p-6 overflow-auto">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl bg-card p-8 md:p-12 border border-border/50 shadow-sm text-center">
          {/* Animated Clock Icon */}
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
            <div className="relative w-full h-full rounded-full bg-amber-500/10 flex items-center justify-center">
              <ClockIcon className="size-10 text-amber-500" />
            </div>
          </div>

          <h1 className="text-2xl font-bold mb-2">Application Under Review</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Thanks for applying to the Nordex Affiliate Program! We're reviewing your application
            and will get back to you soon.
          </p>

          {/* Status Timeline */}
          <div className="space-y-4 text-left max-w-sm mx-auto mb-8">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                <CheckCircle2Icon className="size-4 text-emerald-500" />
              </div>
              <div>
                <p className="text-sm font-medium">Application Submitted</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(affiliate.createdAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  Under Review
                </p>
                <p className="text-xs text-muted-foreground">
                  Usually takes 24-48 hours
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 opacity-50">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                <MailIcon className="size-3.5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Approval Notification</p>
                <p className="text-xs text-muted-foreground">
                  You'll receive an email when approved
                </p>
              </div>
            </div>
          </div>

          {/* Application Details */}
          {affiliate.applicationNote && (
            <div className="bg-muted/30 rounded-lg p-4 text-left mb-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Your Application Note
              </p>
              <p className="text-sm">{affiliate.applicationNote}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-left">
            <p className="text-sm text-blue-600 dark:text-blue-400">
              <strong>Referral Code Reserved:</strong> Your referral code{" "}
              <span className="font-mono font-bold">{affiliate.refCode}</span> has been reserved
              and will be activated upon approval.
            </p>
          </div>
        </div>

        {/* What's Next */}
        <div className="mt-6 rounded-xl bg-card p-6 border border-border/50 shadow-sm">
          <h2 className="text-sm font-semibold mb-4">While You Wait</h2>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2Icon className="size-4 text-emerald-500 shrink-0 mt-0.5" />
              Prepare your promotional content and social media posts
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2Icon className="size-4 text-emerald-500 shrink-0 mt-0.5" />
              Familiarize yourself with Nordex's features and benefits
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2Icon className="size-4 text-emerald-500 shrink-0 mt-0.5" />
              Start trading on Nordex to better understand the platform
            </li>
          </ul>
        </div>
      </div>
    </main>
  );
};

